import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useMeasure } from 'react-use';

import { Box, Flex, useDisclosure } from '@frontend/legacy-components';

import { PageTitle } from 'components/PageTitle';

import { MicroAppHeader } from 'containers/Layouts/DashboardLayout/MicroAppHeader';

import Viewer from 'modules/common/viewer/Viewer';
import CADViewerInfo from 'modules/common/viewer/cadViewer/CADViewerInfo';
import CADViewerTools from 'modules/common/viewer/cadViewer/CADViewerTools';
import { LegacyDxfViewerToggleProvider } from 'modules/common/viewer/context/LegacyDxfViewerToggleContext';
import { getActiveFile } from 'modules/common/viewer/data/reducer';
import { DxfViewerProvider } from 'modules/common/viewer/dxfViewer/DxfViewerContext';
import { DxfViewerTools } from 'modules/common/viewer/dxfViewer/DxfViewerTools';
import { DxfSidePanelsHost } from 'modules/common/viewer/dxfViewer/components/DxfSidePanelHost';
import { useCanDownloadPdf } from 'modules/common/viewer/hooks/featureConfig/useCanDownloadPdf';
import { useActiveViewerKind } from 'modules/common/viewer/hooks/useActiveViewerKind';
import {
  WebViewerContextProvider,
  useWebViewerContext,
} from 'modules/common/viewer/pdfViewer/WebViewerContext';
import {
  usePrintUnitsFeature,
  useUnitsPicker,
} from 'modules/common/viewer/pdfViewer/printUnitsFeature';
import { buildStandaloneViewerConfig } from 'modules/common/viewer/pdfViewer/viewerConfig';
import { VIEWER_KINDS } from 'modules/common/viewer/resolveViewerKind';
import { ExtractedTableProvider } from 'modules/extractedTables/ExtractedTableContext';
import { ExtractedTableModalContainer } from 'modules/extractedTables/ExtractedTableModalContainer';
import { SelectedTableProvider } from 'modules/extractedTables/SelectedTableContext';
import { useCanEditPartInQuote } from 'modules/quotePartViewer/hooks/useCanEditPartInQuote';

import { UnitSystemProvider } from 'utils/measurement/context';

import FileViewerCadTree from './components/FileViewerCadTree';
import { FileViewerHeader } from './components/FileViewerHeader';
import { UpdateItarModal } from './components/UpdateItarModal';
import { getActiveRootFileName } from './data/reducer';
import { useRefreshStaleQueriesOnMount } from './hooks/useRefreshStaleQueriesOnMount';
import { SfvPdfInteractionsProvider } from './hooks/useSfvPdfInteractions';

const FileViewerWrapped = () => {
  const { instance } = useWebViewerContext();
  const viewerKind = useActiveViewerKind();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const rootFileName = useSelector(getActiveRootFileName);
  const { canEdit: canEditPart, disabledReason: canEditPartDisabledReason } =
    useCanEditPartInQuote();
  const [cadViewerRegion, { height: cadViewerRegionHeight = 1 }] =
    useMeasure<HTMLDivElement>();

  useRefreshStaleQueriesOnMount();

  // PDF Viewer Features
  const { printUnits, handleUpdatePrintUnits } = usePrintUnitsFeature();
  useUnitsPicker({
    instance,
    printUnits,
    onUpdatePrintUnits: handleUpdatePrintUnits,
    canUpdatePrintUnits: canEditPart,
    disabledTooltip: canEditPartDisabledReason,
  });

  const canDownloadPdf = useCanDownloadPdf();
  const viewerConfig = useMemo(
    () => buildStandaloneViewerConfig({ canDownloadPdf }),
    [canDownloadPdf],
  );

  const hasViewerTools =
    viewerKind.kind === VIEWER_KINDS.DXF_SCENE ||
    viewerKind.kind === VIEWER_KINDS.MODEL;

  //Note: FileViewerCadTree receives parentHeight so it knows the maxHeight of the floating cad tree
  return (
    <Flex
      direction="column"
      overflow="hidden"
      position="fixed"
      height="100%"
      width="100%"
    >
      <ExtractedTableModalContainer />
      <PageTitle title={rootFileName} />
      <MicroAppHeader />
      <FileViewerHeader onOpen={onOpen} />
      <UpdateItarModal isOpen={isOpen} onClose={onClose} />
      <Flex height="100%" width="100%" overflowX="hidden">
        <Box
          background="linear-gradient(150deg, #fff 0%, #cccccc 100%)"
          flexGrow={1}
          overflow="hidden"
          position="relative"
          ref={cadViewerRegion}
          className="viewer-wrapper"
          sx={{
            containerType: 'inline-size',
            containerName: 'viewer-content',
          }}
        >
          {viewerKind.kind === VIEWER_KINDS.MODEL && (
            <FileViewerCadTree
              top={1.25}
              left={1.5}
              minWidth="240px"
              parentHeight={cadViewerRegionHeight}
              sx={{
                '@container viewer-content (max-width: 1064px)': {
                  top: '4.25rem',
                },
              }}
            />
          )}
          {hasViewerTools && (
            <Flex
              direction="column"
              position="absolute"
              top={0}
              zIndex={2}
              w="100%"
              alignItems="center"
              p={1.25}
              gap={1.5}
              pointerEvents="none"
              className="viewer-tools-wrapper"
            >
              {viewerKind.kind === VIEWER_KINDS.MODEL && <CADViewerTools />}
              {viewerKind.kind === VIEWER_KINDS.DXF_SCENE && <DxfViewerTools />}
            </Flex>
          )}
          {viewerKind.kind === VIEWER_KINDS.MODEL && <CADViewerInfo />}
          {viewerKind.kind === VIEWER_KINDS.DXF_SCENE && (
            <DxfSidePanelsHost
              position="absolute"
              zIndex={4}
              width="240px"
              top={1.25}
              left={1.5}
              sx={{
                '@container viewer-content (max-width: 860px)': {
                  top: '4.25rem',
                },
              }}
            />
          )}
          <Viewer
            config={viewerConfig}
            leftOffset="300px"
            viewerKind={viewerKind}
            canEditPartInQuoteDisabledReason={canEditPartDisabledReason}
          />
        </Box>
      </Flex>
    </Flex>
  );
};

const FileViewer = () => {
  const activeFile = useSelector(getActiveFile);
  const partFileUuids = useMemo(
    () => (activeFile?.uuid ? [activeFile.uuid] : []),
    [activeFile],
  );
  return (
    <UnitSystemProvider>
      <WebViewerContextProvider>
        <DxfViewerProvider>
          <LegacyDxfViewerToggleProvider
            partFileUuid={activeFile?.uuid ?? null}
          >
            <ExtractedTableProvider partFileUuids={partFileUuids}>
              <SelectedTableProvider>
                <SfvPdfInteractionsProvider>
                  <FileViewerWrapped />
                </SfvPdfInteractionsProvider>
              </SelectedTableProvider>
            </ExtractedTableProvider>
          </LegacyDxfViewerToggleProvider>
        </DxfViewerProvider>
      </WebViewerContextProvider>
    </UnitSystemProvider>
  );
};

export default FileViewer;
